import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  TextField,
  Select,
  Checkbox,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Box,
  Divider,
  Thumbnail,
} from '@shopify/polaris';
import { clinicPortalApi, AddOnProduct } from '@/services';
import AddOnOptionSelects from '@/components/AddOnOptionSelects';
import { splitZamst, ZAMST_HEADING } from '@/utils/addonOptions';
import { minRentalStartDate } from '@/utils/businessDays';

interface RentalProduct {
  shopifyProductId: string;
  title: string;
  handle: string;
  imageUrl: string | null;
  variants: { id: string; title: string; sku: string; price: string }[];
  rentalConfig: {
    isRentalProduct: boolean;
    weeklyRateCents: number | null;
    monthlyRateCents: number | null;
    bilateralPriceCents: number | null;
    wholesaleWeeklyRateCents: number | null;
    wholesaleMonthlyRateCents: number | null;
    wholesaleBilateralPriceCents: number | null;
    minRentalWeeks: number;
  };
}

interface WrapProduct {
  shopifyProductId: string;
  title: string;
  imageUrl: string | null;
  variants: { id: string; title: string; sku: string }[];
}

interface WrapSelection {
  productId: string;
  productTitle: string;
  variantId?: string;
  variantTitle?: string;
  sku?: string;
}

const WEEKS_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  label: `${i + 2}`,
  value: `${i + 2}`,
}));

export default function ClinicCreateOrderDetail() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId') || '';
  const productId = searchParams.get('productId') || '';

  const { data: products, isLoading } = useQuery<RentalProduct[]>({
    queryKey: ['clinic-products'],
    queryFn: () => clinicPortalApi.getProducts(),
  });

  const { data: wraps, isLoading: wrapsLoading } = useQuery<WrapProduct[]>({
    queryKey: ['clinic-wraps'],
    queryFn: () => clinicPortalApi.getWraps(),
  });

  const product = useMemo(
    () => products?.find((p) => p.shopifyProductId === productId),
    [products, productId],
  );

  const sortedWraps = useMemo(() => {
    return [...(wraps || [])].sort((a, b) => a.title.localeCompare(b.title));
  }, [wraps]);

  // Form state
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [surgeryDate, setSurgeryDate] = useState('');
  const [rentalWeeks, setRentalWeeks] = useState('2');
  const [isBilateral, setIsBilateral] = useState(false);

  // Wrap selection
  const [selectedWrapId, setSelectedWrapId] = useState<string>('');
  const [wrapVariantByProduct, setWrapVariantByProduct] = useState<Record<string, string>>({});

  const selectedWrap = useMemo(
    () => sortedWraps.find((w) => w.shopifyProductId === selectedWrapId),
    [sortedWraps, selectedWrapId],
  );
  const wrapVariants = selectedWrap?.variants || [];
  const isMultiVariantWrap = wrapVariants.length > 1;
  const selectedWrapVariantId = isMultiVariantWrap
    ? (wrapVariantByProduct[selectedWrapId] || wrapVariants[0]?.id || '')
    : (wrapVariants[0]?.id || '');
  const selectedWrapVariant = wrapVariants.find((v) => v.id === selectedWrapVariantId);

  const setWrapVariantId = useCallback(
    (variantId: string) => {
      if (!selectedWrapId) return;
      setWrapVariantByProduct((prev) => ({ ...prev, [selectedWrapId]: variantId }));
    },
    [selectedWrapId],
  );

  // Add-on catalog (Shopify products tagged `rental-addon`)
  const { data: addOnCatalog } = useQuery<AddOnProduct[]>({
    queryKey: ['clinic-addons'],
    queryFn: () => clinicPortalApi.getAddons(),
  });
  // Selected add-ons: productId → chosen variantId. Absent key = not selected.
  const [selectedAddOnVariants, setSelectedAddOnVariants] = useState<Map<string, string>>(new Map());

  const toggleAddOn = useCallback((productId: string, checked: boolean, defaultVariantId: string) => {
    setSelectedAddOnVariants((prev) => {
      const next = new Map(prev);
      if (checked) next.set(productId, defaultVariantId);
      else next.delete(productId);
      return next;
    });
  }, []);

  const setAddOnVariant = useCallback((productId: string, variantId: string) => {
    setSelectedAddOnVariants((prev) => {
      const next = new Map(prev);
      if (next.has(productId)) next.set(productId, variantId);
      return next;
    });
  }, []);

  // Auto-select first variant
  const variants = product?.variants || [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) || variants[0];
  const variantOptions = variants.map((v) => ({ label: v.title, value: v.id }));

  // Use wholesale pricing if available, fall back to retail
  const weeklyRateDollars = product?.rentalConfig.wholesaleWeeklyRateCents
    ? product.rentalConfig.wholesaleWeeklyRateCents / 100
    : product?.rentalConfig.weeklyRateCents
      ? product.rentalConfig.weeklyRateCents / 100
      : 0;
  const bilateralPriceDollars = product?.rentalConfig.wholesaleBilateralPriceCents
    ? product.rentalConfig.wholesaleBilateralPriceCents / 100
    : product?.rentalConfig.bilateralPriceCents
      ? product.rentalConfig.bilateralPriceCents / 100
      : 0;
  const minWeeks = product?.rentalConfig.minRentalWeeks || 2;

  const weeks = parseInt(rentalWeeks, 10) || minWeeks;
  const rentalTotal = weeklyRateDollars * weeks;
  const bilateralTotal = isBilateral ? bilateralPriceDollars : 0;

  // Normalize the catalog into the line shape we submit + display.
  // Clinics pay wholesale; fall back to retail if no wholesale metafield is set.
  const addOnLines = useMemo(
    () =>
      (addOnCatalog || []).map((a) => {
        const selectedVariantId = selectedAddOnVariants.get(a.shopifyProductId);
        const isSelected = !!selectedVariantId;
        const chosenVariant =
          (selectedVariantId && a.variants.find((v) => v.id === selectedVariantId)) || a.variants[0];
        const wholesale =
          ((chosenVariant?.wholesalePriceCents ?? a.wholesalePriceCents ?? a.retailPriceCents ?? 0)) / 100;
        const retail = (a.retailPriceCents ?? 0) / 100;
        return {
          id: a.shopifyProductId,
          name: a.title,
          vendor: a.vendor ?? null,
          options: a.options,
          variants: a.variants,
          chosenVariantId: chosenVariant?.id ?? '',
          sku: chosenVariant?.sku ?? '',
          wholesalePrice: wholesale,
          retailPrice: retail,
          selected: isSelected,
        };
      }),
    [addOnCatalog, selectedAddOnVariants],
  );

  const addOnTotal = addOnLines.filter((a) => a.selected).reduce((sum, a) => sum + a.wholesalePrice, 0);
  const subtotal = rentalTotal + bilateralTotal + addOnTotal;

  const wrapValid = !!(selectedWrap && (!isMultiVariantWrap || selectedWrapVariant));
  const isValid =
    surgeryDate &&
    surgeryDate >= minRentalStartDate() &&
    weeks >= minWeeks &&
    product &&
    wrapValid;

  const handleProceedToCheckout = useCallback(() => {
    if (!isValid || !product || !selectedWrap) return;

    const variant = selectedVariant || variants[0];
    const wrap: WrapSelection = {
      productId: selectedWrap.shopifyProductId,
      productTitle: selectedWrap.title,
      variantId: selectedWrapVariant?.id,
      variantTitle: isMultiVariantWrap ? selectedWrapVariant?.title : undefined,
      sku: selectedWrapVariant?.sku || undefined,
    };
    const orderData = {
      patientId,
      productId: product.shopifyProductId,
      productName: product.title,
      productSku: variant?.sku || '',
      variantId: variant?.id || '',
      size: variant?.title || '',
      weeklyRate: weeklyRateDollars,
      surgeryDate,
      rentalWeeks: weeks,
      isBilateral,
      bilateralPrice: bilateralPriceDollars,
      addOns: addOnLines
        .filter((a) => a.selected)
        .map((a) => ({ name: a.name, sku: a.sku, price: a.wholesalePrice, quantity: 1 })),
      wrap,
      subtotal,
    };

    // Store in sessionStorage and navigate to checkout
    sessionStorage.setItem('clinicOrderData', JSON.stringify(orderData));
    navigate(`${base}/orders/new/checkout?patientId=${patientId}`);
  }, [isValid, product, selectedWrap, selectedWrapVariant, isMultiVariantWrap, selectedVariant, variants, patientId, weeklyRateDollars, surgeryDate, weeks, isBilateral, bilateralPriceDollars, addOnLines, subtotal, navigate]);

  if (isLoading) {
    return (
      <Page title="Product Detail" backAction={{ content: 'Products', onAction: () => navigate(-1) }}>
        <Card><SkeletonBodyText lines={10} /></Card>
      </Page>
    );
  }

  if (!product) {
    return (
      <Page title="Product Not Found" backAction={{ content: 'Products', onAction: () => navigate(-1) }}>
        <Banner tone="critical"><p>Product not found.</p></Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Create Order"
      subtitle="Product Detail"
      backAction={{ content: 'Products', onAction: () => navigate(`${base}/orders/new?patientId=${patientId}`) }}
    >
      <Card>
        <InlineStack gap="800" wrap={false} blockAlign="start">
          {/* Product Image */}
          <Box minWidth="300px" maxWidth="400px">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.title}
                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
              />
            ) : (
              <Box background="bg-surface-secondary" padding="1200" borderRadius="200">
                <Text as="p" alignment="center" tone="subdued">No image</Text>
              </Box>
            )}
          </Box>

          {/* Product Configuration */}
          <Box width="100%">
            <BlockStack gap="400">
              <Text as="h2" variant="headingXl">{product.title}</Text>
              {selectedVariant && (
                <Text as="p" tone="subdued">{selectedVariant.title}</Text>
              )}
              <Text as="p" variant="headingLg" tone="success">
                ${weeklyRateDollars.toFixed(2)} CAD / Week (*Minimum {minWeeks} weeks)
              </Text>

              <Divider />

              {/* Size / Variant */}
              {variantOptions.length > 1 && (
                <Select
                  label="Size"
                  options={variantOptions}
                  value={selectedVariantId || variants[0]?.id}
                  onChange={setSelectedVariantId}
                  requiredIndicator
                />
              )}

              {/* Surgery Date & Weeks */}
              <InlineStack gap="400">
                <Box width="50%">
                  <TextField
                    label="Surgery Date"
                    type="date"
                    value={surgeryDate}
                    onChange={setSurgeryDate}
                    autoComplete="off"
                    requiredIndicator
                    min={minRentalStartDate()}
                    error={
                      surgeryDate && surgeryDate < minRentalStartDate()
                        ? `Earliest start is ${minRentalStartDate()} (3 business days lead time)`
                        : undefined
                    }
                  />
                </Box>
                <Box width="50%">
                  <Select
                    label="No. of Weeks"
                    options={WEEKS_OPTIONS}
                    value={rentalWeeks}
                    onChange={setRentalWeeks}
                    requiredIndicator
                  />
                </Box>
              </InlineStack>

              <Divider />

              {/* Wrap selector */}
              <BlockStack gap="200">
                <Text as="p" variant="headingMd">
                  Select Wrap <Text as="span" tone="critical">*</Text>
                </Text>
                <Text as="p" tone="subdued">
                  Choose the wrap that ships with the machine.
                </Text>

                {wrapsLoading ? (
                  <SkeletonBodyText lines={4} />
                ) : sortedWraps.length === 0 ? (
                  <Banner tone="warning">
                    <p>No wraps available. Contact HealFaster support.</p>
                  </Banner>
                ) : (
                  <InlineGrid columns={{ xs: 2, sm: 3, md: 3 }} gap="300">
                    {sortedWraps.map((wrap) => {
                      const isSelected = wrap.shopifyProductId === selectedWrapId;
                      return (
                        <div
                          key={wrap.shopifyProductId}
                          onClick={() => setSelectedWrapId(wrap.shopifyProductId)}
                          style={{
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #008060' : '2px solid transparent',
                            borderRadius: '8px',
                            padding: '4px',
                          }}
                        >
                          <Card padding="300">
                            <BlockStack gap="200" inlineAlign="center">
                              {wrap.imageUrl ? (
                                <img
                                  src={wrap.imageUrl}
                                  alt={wrap.title}
                                  style={{
                                    width: '100%',
                                    maxWidth: '120px',
                                    height: 'auto',
                                    display: 'block',
                                  }}
                                />
                              ) : (
                                <Thumbnail source="" alt={wrap.title} size="medium" />
                              )}
                              <Text as="p" variant="bodySm" alignment="center">
                                {wrap.title}
                              </Text>
                              {wrap.variants.length > 1 && (
                                <Badge tone="info">{`${wrap.variants.length} options`}</Badge>
                              )}
                              {selectedWrapId === wrap.shopifyProductId && wrap.variants.length > 1 && (
                                <Select
                                  label="Wrap variant"
                                  options={wrap.variants.map((v) => ({ label: v.title, value: v.id }))}
                                  value={wrapVariantByProduct[wrap.shopifyProductId] ?? wrap.variants[0]?.id ?? ''}
                                  onChange={(value) => setWrapVariantId(value)}
                                />
                              )}
                            </BlockStack>
                          </Card>
                        </div>
                      );
                    })}
                  </InlineGrid>
                )}

                {selectedWrap && (
                  <Text as="p" tone="success">
                    Selected: {selectedWrap.title}
                    {isMultiVariantWrap && selectedWrapVariant ? ` — ${selectedWrapVariant.title}` : ''}
                  </Text>
                )}
              </BlockStack>

              {/* Bilateral — placed after wrap selection so the operator picks the wrap first */}
              {bilateralPriceDollars > 0 && (
                <BlockStack gap="100">
                  <Checkbox
                    label={`Bilateral ($${bilateralPriceDollars.toFixed(2)} CAD - one time charge)`}
                    checked={isBilateral}
                    onChange={setIsBilateral}
                  />
                  {isBilateral && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Two of the selected wrap will ship. Recommended only for true bilateral cases (e.g. both knees).
                    </Text>
                  )}
                </BlockStack>
              )}

              <Divider />

              {/* Add-on products */}
              <Text as="p" tone="subdued">Please Select the Add-On products</Text>

              <BlockStack gap="300">
                {addOnLines.length === 0 && (
                  <Text as="p" tone="subdued">No add-ons available.</Text>
                )}
                {(() => {
                  const renderAddOn = (addOn: (typeof addOnLines)[number]) => {
                    const hasMultipleVariants = addOn.variants.length > 1;
                    const priceFor = (v: (typeof addOn.variants)[number]) => {
                      const wholesaleCents = v.wholesalePriceCents ?? null;
                      return (wholesaleCents != null ? wholesaleCents : Math.round(parseFloat(v.price || '0') * 100)) / 100;
                    };
                    return (
                      <Card key={addOn.id}>
                        <BlockStack gap="200">
                          <Text as="p" variant="bodyMd">
                            Add <Text as="span" fontWeight="bold">{addOn.name}</Text>{' '}
                            ${addOn.wholesalePrice.toFixed(2)} CAD / ${addOn.retailPrice.toFixed(2)} CAD
                          </Text>
                          <InlineStack gap="400">
                            <Checkbox
                              label="Yes"
                              checked={addOn.selected}
                              onChange={(checked) => toggleAddOn(addOn.id, checked, addOn.variants[0]?.id ?? '')}
                            />
                          </InlineStack>
                          {addOn.selected && hasMultipleVariants && (
                            <AddOnOptionSelects
                              options={addOn.options}
                              variants={addOn.variants}
                              value={addOn.chosenVariantId}
                              onChange={(value) => setAddOnVariant(addOn.id, value)}
                              legacyLabel={(v) => `${v.title} — $${priceFor(v as (typeof addOn.variants)[number]).toFixed(2)} CAD`}
                            />
                          )}
                        </BlockStack>
                      </Card>
                    );
                  };
                  const { regular, zamst } = splitZamst(addOnLines, (a) => a.name);
                  return (
                    <>
                      {regular.map(renderAddOn)}
                      {zamst.length > 0 && (
                        <>
                          <Text as="h3" variant="headingSm">{ZAMST_HEADING}</Text>
                          {zamst.map(renderAddOn)}
                        </>
                      )}
                    </>
                  );
                })()}
              </BlockStack>

              <Divider />

              {/* Price Summary */}
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span">Rental ({weeks} weeks x ${weeklyRateDollars.toFixed(2)}):</Text>
                  <Text as="span">${rentalTotal.toFixed(2)} CAD</Text>
                </InlineStack>
                {isBilateral && (
                  <InlineStack align="space-between">
                    <Text as="span">Bilateral:</Text>
                    <Text as="span">${bilateralTotal.toFixed(2)} CAD</Text>
                  </InlineStack>
                )}
                {addOnLines.filter((a) => a.selected).map((a) => {
                  const chosen = a.variants.find((v) => v.id === a.chosenVariantId);
                  const label = chosen && a.variants.length > 1 ? `${a.name} (${chosen.title})` : a.name;
                  return (
                    <InlineStack key={a.id} align="space-between">
                      <Text as="span">{label}:</Text>
                      <Text as="span">${a.wholesalePrice.toFixed(2)} CAD</Text>
                    </InlineStack>
                  );
                })}
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" fontWeight="bold">Subtotal:</Text>
                  <Text as="span" fontWeight="bold">${subtotal.toFixed(2)} CAD</Text>
                </InlineStack>
              </BlockStack>

              <Button
                variant="primary"
                onClick={handleProceedToCheckout}
                disabled={!isValid}
                fullWidth
              >
                Proceed to Checkout
              </Button>
            </BlockStack>
          </Box>
        </InlineStack>
      </Card>
    </Page>
  );
}
